import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const PaymentTemplateModal = ({ template, language, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    phases: [
      { phaseNumber: 1, phaseName: '', percentage: 0, daysOffset: 0 }
    ]
  });

  useEffect(() => {
    if (template) {
      setFormData({
        name: template?.name || '',
        description: template?.description || '',
        isActive: template?.isActive !== undefined ? template?.isActive : true,
        phases: template?.phases || [{ phaseNumber: 1, phaseName: '', percentage: 0, daysOffset: 0 }]
      });
    }
  }, [template]);

  const handleAddPhase = () => {
    setFormData({
      ...formData,
      phases: [
        ...formData?.phases,
        { 
          phaseNumber: formData?.phases?.length + 1, 
          phaseName: '', 
          percentage: 0, 
          daysOffset: 0 
        }
      ]
    });
  };

  const handleRemovePhase = (index) => {
    const newPhases = formData?.phases?.filter((_, i) => i !== index);
    const renumberedPhases = newPhases?.map((phase, i) => ({ ...phase, phaseNumber: i + 1 }));
    setFormData({ ...formData, phases: renumberedPhases });
  };

  const handlePhaseChange = (index, field, value) => {
    const newPhases = [...formData?.phases];
    newPhases[index] = { ...newPhases?.[index], [field]: value };
    setFormData({ ...formData, phases: newPhases });
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    const totalPercentage = formData?.phases?.reduce((sum, phase) => sum + parseFloat(phase?.percentage || 0), 0);
    
    if (Math.abs(totalPercentage - 100) > 0.01) {
      alert(`Le total des pourcentages doit être 100% (actuellement ${totalPercentage?.toFixed(2)}%)`);
      return;
    }

    onSave(formData);
  };

  const totalPercentage = formData?.phases?.reduce((sum, phase) => sum + parseFloat(phase?.percentage || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="text-xl font-semibold text-foreground">
            {template ? 'Modifier le template' : 'Nouveau template'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <Input
            label="Nom du template"
            value={formData?.name}
            onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
            required
          />

          <Input
            label="Description"
            value={formData?.description}
            onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
          />

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isActive"
              checked={formData?.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e?.target?.checked })}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-foreground">
              Template actif
            </label>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                Phases de paiement
              </h3>
              <Button type="button" variant="outline" size="sm" onClick={handleAddPhase}>
                <Icon name="Plus" size={16} className="mr-2" />
                Ajouter phase
              </Button>
            </div>

            {formData?.phases?.map((phase, index) => (
              <div key={index} className="bg-muted/30 border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                      {phase?.phaseNumber}
                    </div>
                    <span className="font-medium text-foreground">
                      Phase {phase?.phaseNumber}
                    </span>
                  </div>
                  {formData?.phases?.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemovePhase(index)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                    >
                      <Icon name="Trash2" size={16} className="text-error" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input
                    label="Nom de la phase"
                    value={phase?.phaseName}
                    onChange={(e) => handlePhaseChange(index, 'phaseName', e?.target?.value)}
                    required
                  />
                  <Input
                    label="Pourcentage (%)"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={phase?.percentage}
                    onChange={(e) => handlePhaseChange(index, 'percentage', parseFloat(e?.target?.value) || 0)}
                    required
                  />
                  <Input
                    label="Délai (jours)"
                    type="number"
                    min="0"
                    value={phase?.daysOffset}
                    onChange={(e) => handlePhaseChange(index, 'daysOffset', parseInt(e?.target?.value) || 0)}
                    required
                  />
                </div>
              </div>
            ))}

            <div className={`flex items-center justify-between p-4 rounded-lg border ${
              Math.abs(totalPercentage - 100) < 0.01
                ? 'bg-success/10 border-success/30' :'bg-warning/10 border-warning/30'
            }`}>
              <span className="font-medium text-foreground">
                Total des pourcentages
              </span>
              <span className={`text-xl font-bold ${
                Math.abs(totalPercentage - 100) < 0.01 ? 'text-success' : 'text-warning'
              }`}>
                {totalPercentage?.toFixed(2)}%
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-4">
            <Button type="submit" fullWidth>
              Enregistrer
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={onClose}>
              Annuler
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentTemplateModal;